import { z } from 'zod'
import { innerInstructionSchema } from './innerInstruction'
import { tokenBalanceSchema } from './tokenBalance'
import { transactionSchema } from './transaction'
import {
  nativeTokenTransferSchema,
  splTokenTransferSchema,
  tokenTransferSchema,
} from './transfer'

export const transactionDetailSchema = z.object({
  /** the slot this transaction was processed in */
  slot: z.number().int(),
  /** Transaction object, either in JSON format or encoded binary data, depending on encoding parameter */
  transaction: transactionSchema,
  /** estimated production time, as Unix timestamp (seconds since the Unix epoch) of when the transaction was processed. null if not available */
  blockTime: z.number().int().nullable(),
  /** transaction status metadata object */
  meta: z
    .object({
      /** Error if transaction failed, null if transaction succeeded. */
      err: z.unknown().nullable(),
      /** fee this transaction was charged, as u64 integer */
      fee: z.number().int(),
      /** array of u64 account balances from before the transaction was processed */
      preBalances: z.array(z.number().int()),
      /** array of u64 account balances after the transaction was processed */
      postBalances: z.array(z.number().int()),
      /** List of inner instructions or null if inner instruction recording was not enabled during this transaction */
      innerInstructions: z.array(innerInstructionSchema).nullable(),
      /** List of token balances from before the transaction was processed or omitted if token balance recording was not yet enabled during this transaction */
      preTokenBalances: z.array(tokenBalanceSchema).optional(),
      /** List of token balances from after the transaction was processed or omitted if token balance recording was not yet enabled during this transaction */
      postTokenBalances: z.array(tokenBalanceSchema).optional(),
      /** array of string log messages or null if log message recording was not enabled during this transaction */
      logMessages: z.array(z.string()).nullable(),
      /** transaction-level rewards, populated if rewards are requested; an array of JSON objects containing */
      rewards: z
        .array(
          z.object({
            /** The public key, as base-58 encoded string, of the account that received the reward */
            pubkey: z.string(),
            /** number of reward lamports credited or debited by the account, as a i64 */
            lamports: z.number(),
            /** account balance in lamports after the reward was applied */
            postBalance: z.number(),
            /** type of reward: currently only "rent", other types may be added in the future */
            rewardType: z.string(),
            /** vote account commission when the reward was credited, only present for voting and staking rewards */
            commission: z.number().optional().nullable(),
          }),
        )
        .nullable(),
      /** Transaction addresses loaded from address lookup tables. Undefined if maxSupportedTransactionVersion is not set in request params, or if jsonParsed encoding is set in request params. */
      loadedAddresses: z
        .object({
          /** Ordered list of base-58 encoded addresses for writable loaded accounts */
          writable: z.array(z.string()),
          /** Ordered list of base-58 encoded addresses for readonly loaded accounts */
          readonly: z.array(z.string()),
        })
        .optional(),
      /** the most-recent return data generated by an instruction in the transaction */
      returnData: z
        .object({
          /** the program that generated the return data, as base-58 encoded Pubkey */
          programId: z.string(),
          /** the return data itself, as base-64 encoded binary data */
          data: z.array(z.string()),
        })
        .optional(),
      /** number of compute units consumed by the transaction */
      computeUnitsConsumed: z.number().int().optional(),
    })
    .nullable(),
  /** Transaction version. Undefined if maxSupportedTransactionVersion is not set in request params. */
  version: z.union([z.literal('legacy'), z.number()]).optional(),
  /** List of sol transfers from sol balances */
  solTransfers: z.array(nativeTokenTransferSchema).optional().nullable(),
  /** List of token transfers from balances */
  tokenTransfersFromBalances: z.array(splTokenTransferSchema).optional(),
  /** List of token transfers from inner instructions */
  tokenTransfersFromInstructions: z.array(tokenTransferSchema).optional(),
})

export type TransactionDetail = Omit<
  z.infer<typeof transactionDetailSchema>,
  'blockTime'
> & { blockTime: number }
